import { createQueryClient, dataSourceFor, initAppearance, initTheme } from '@ctcells/core'
import { createDemoStreamFactory, exposeDemoStreamControls } from '@ctcells/fixtures/ws'
import { RealtimeProvider, type StreamConnectionFactory } from '@ctcells/realtime'
import { QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider, type RouteObject } from 'react-router'

import { AppErrorBoundary } from './shell/AppErrorBoundary.tsx'
import { AppRuntime } from './shell/AppRuntime.tsx'
import { applyDesignViewport } from './tokens/designViewport.ts'
import { applySkin, type Skin } from './tokens/skin.ts'

/**
 * 🔴 구독이 목/실물로 갈리는 **유일한 지점**이다(`dataSourceFor('streams')`).
 * 위쪽(stale 판정 · gate · 화면)은 어느 쪽이든 완전히 같은 코드를 탄다.
 *
 * `demo` 인 사유: 서버에 WebSocket 라우트가 0건이다(2026-09-17 실측). 정본
 * `events.asyncapi.yaml` 에 선언은 있으나 **미구현**이다 — 실물이 서면 `.env` 한 줄
 * (`VITE_DATA_SOURCE=streams=live`)로 되돌린다. 코드는 고치지 않는다.
 */
function resolveStreamFactory(): StreamConnectionFactory | undefined {
  if (dataSourceFor('streams') !== 'demo') return undefined

  const { factory, controls } = createDemoStreamFactory()
  if (import.meta.env.DEV) exposeDemoStreamControls(controls)
  return factory
}

/** 앱이 넘기는 유일한 겉모습 선택지. 안 넘기면 종전 흑백 토큰이다. */
export interface MountOptions {
  /** `<html data-skin>` 에 박는다. 지금은 통합 콘솔만 `'console'` 을 넘긴다 */
  skin?: Skin
}

/**
 * 앱 5개가 공유하는 엔트리. 앱마다 다른 것은 넘겨받는 라우트 표와 스킨뿐이다.
 * 구독(RealtimeProvider)과 서버 상태 캐시는 라우터보다 위에 있다 —
 * 화면이 바뀌어도 WebSocket 연결은 유지된다(연결의 주인은 앱 셸).
 */
export function mountApp(routes: RouteObject[], options: MountOptions = {}): void {
  const container = document.getElementById('root')
  if (!container) throw new Error('#root 엘리먼트를 찾지 못했다')

  // 첫 렌더 전에 테마·스킨을 확정한다 — 뒤로 미루면 잘못된 겉모습이 한 프레임 번쩍인다.
  initTheme()
  if (options.skin) {
    applySkin(document.documentElement, options.skin)
    /*
     * 겉모습 «취향»(강조색)은 스킨이 있는 앱에만 단다. 스킨 없는 앱(장비 관점 · 모듈 3종)에
     * 속성을 달면 계단이 없는 변수를 가리키게 되어 강조가 통째로 빈다.
     */
    initAppearance()
  }
  applyDesignViewport(document.documentElement)

  const queryClient = createQueryClient()
  const router = createBrowserRouter(routes)
  const createConnection = resolveStreamFactory()

  createRoot(container).render(
    <StrictMode>
      {/*
       * 🔴 **최상위 경계.** 여기 없으면 셸이 던진 예외에 React 가 트리를 통째로 언마운트해
       *    «백지»가 된다. 화면 하나가 던진 것은 이 아래 라우트 단 `errorElement`
       *    (`RouteErrorScreen`)가 **셸 안에서** 받으므로 여기까지 오지 않는다.
       * ⚠ 제공자(캐시·구독)보다 «밖»이다 — 제공자가 던져도 받아야 하기 때문이다.
       */}
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RealtimeProvider createConnection={createConnection}>
            {/* 세션 복구 · 구독→캐시 다리. 라우터보다 위에 있어야 화면 전환에도 살아 있다 */}
            <AppRuntime />
            <RouterProvider router={router} />
          </RealtimeProvider>
        </QueryClientProvider>
      </AppErrorBoundary>
    </StrictMode>,
  )
}
